import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import apiCoreAdapter from '../../../../hooks/apiCoreAdapter';
import { getSessionData } from '../../../../hooks/api';
import { getImpersonatorId, isImpersonated } from '../../../../hooks/api/auth/tokenManager';
import { invalidateSessionsCache, invalidatePatientsCache } from '../data';
import { localTzToUTC } from '../timeZoneHelpers';
import { BA_TIMEZONE } from '../../../../utils/timezone';
import { isoToLocalParts } from '../utils';
import {
  getCompatibleAppointmentTypes,
  resolvePatientForScheduling,
  resolveEffectiveAppointmentType,
  validateAppointmentTypeMatch,
} from '../bookingUtils';
import type { AppointmentType } from '../../../../contexts/PsychologistContext';
import type { Patient, SessionWithPsychologist, ScheduleDirectInput } from './useCalendarData';

// ---------------------------------------------------------------------------
// Mock patients used during onboarding tour
// ---------------------------------------------------------------------------

const ONBOARDING_MOCK_PATIENTS: Patient[] = [
  {
    id: 'mock-patient-1',
    name: 'Ana López',
    email: 'ana.lopez@demo.psi',
    phone: '+34 611 234 567',
    process: 'mock-process-individual',
    processType: 'Individual',
    timezone: 'Europe/Madrid',
    nationality: 'ES',
    hasNextSessionDate: false,
  },
  {
    id: 'mock-patient-2',
    name: 'Carlos Ruiz',
    email: 'carlos.ruiz@demo.psi',
    phone: '+34 612 345 678',
    process: 'mock-process-pareja',
    processType: 'Pareja',
    timezone: 'Europe/Madrid',
    nationality: 'ES',
    hasNextSessionDate: false,
  },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseCalendarBookingOptions {
  patients: Patient[];
  appointmentTypes: AppointmentType[];
  editMode: boolean;
  sessions: SessionWithPsychologist[];
  startOfWeek: Date;
  psychologist: { id: string | number; specialties: string[] };
  psychologistId: string | null | undefined;
  selectedTimeZone: string;
  isBusy: boolean;
  blockAll: boolean;
  blockScheduling: boolean;
  isRescheduleMode: boolean;
  mockAvailability?: boolean;
  // External props
  openScheduleModalTick?: number;
  preloadProcessId?: string;
  // Si es true, preselecciona el paciente del preload aunque ya tenga próxima sesión
  // (link compartible de agendar: la selección debe ser siempre automática).
  preloadForceSelect?: boolean;
  cancelSchedulingTick?: number;
  onPreloadConsumed?: () => void;
  onSchedulingModeChange?: (isScheduling: boolean, patientName?: string, processId?: string) => void;
  // External functions / refs
  fetchPatientsOnce: (psychologistId: string) => Promise<Patient[]>;
  patientsCacheRef: React.MutableRefObject<Patient[] | undefined>;
  patientsCachePsychIdRef: React.MutableRefObject<string | undefined>;
  scheduleDirect: (input: ScheduleDirectInput) => { ok: boolean };
  loadData: () => Promise<void>;
  showError: (msg: string) => void;
  showSuccess: (msg: string) => void;
  setPatients: React.Dispatch<React.SetStateAction<Patient[]>>;
}

export interface UseCalendarBookingReturn {
  selectedPatientEmail: string;
  setSelectedPatientEmail: React.Dispatch<React.SetStateAction<string>>;
  selectedProcessId: string;
  /** Selecciona un paciente por su proceso — pasar '' en `email` limpia la selección. */
  selectPatient: (email: string, processId?: string) => void;
  selectedAppointmentTypeId: string;
  setSelectedAppointmentTypeId: React.Dispatch<React.SetStateAction<string>>;
  selectedAppointmentType: AppointmentType | null;
  setSelectedAppointmentType: React.Dispatch<React.SetStateAction<AppointmentType | null>>;
  selectPatientAndTypeModal: { mode: 'schedule' | 'recurring'; rangeKey: string } | null;
  setSelectPatientAndTypeModal: React.Dispatch<
    React.SetStateAction<{ mode: 'schedule' | 'recurring'; rangeKey: string } | null>
  >;
  dragModal: { date: string; startTime: string; endTime: string } | null;
  setDragModal: React.Dispatch<
    React.SetStateAction<{ date: string; startTime: string; endTime: string } | null>
  >;
  setDragModalSafe: (modal: { date: string; startTime: string; endTime: string } | null) => void;
  isBooking: boolean;
  patientsLoading: boolean;
  showDayLimitModal: boolean;
  setShowDayLimitModal: React.Dispatch<React.SetStateAction<boolean>>;
  effectivePatients: Patient[];
  selectedPatient: Patient | undefined;
  compatibleAppointmentTypes: AppointmentType[];
  isSchedulingMode: boolean;
  isSchedulingIntent: boolean;
  handleBookingConfirm: () => Promise<void>;
  dstDates: { start: string; end: string; diffHours: number } | null;
  /** Huso del paciente seleccionado, resuelto desde las sesiones (ver `patientTimezone`). */
  patientTimezone?: string;
}

// ---------------------------------------------------------------------------
// Hook implementation
// ---------------------------------------------------------------------------

export function useCalendarBooking({
  patients,
  appointmentTypes,
  editMode,
  sessions,
  startOfWeek,
  psychologist,
  selectedTimeZone,
  isBusy,
  blockAll,
  blockScheduling,
  isRescheduleMode,
  mockAvailability = false,
  openScheduleModalTick,
  preloadProcessId,
  preloadForceSelect = false,
  cancelSchedulingTick,
  onSchedulingModeChange,
  fetchPatientsOnce,
  patientsCacheRef,
  patientsCachePsychIdRef,
  scheduleDirect,
  loadData,
  showError,
  showSuccess,
  setPatients,
  onPreloadConsumed,
}: UseCalendarBookingOptions): UseCalendarBookingReturn {
  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------

  const [selectedPatientEmail, setSelectedPatientEmail] = useState<string>('');
  // Un paciente puede tener más de un proceso con la misma psicóloga (Individual + Pareja, por
  // ejemplo) y todos comparten el email, así que el proceso es lo que identifica la selección.
  const [selectedProcessId, setSelectedProcessId] = useState<string>('');
  const [selectedAppointmentTypeId, setSelectedAppointmentTypeId] = useState<string>('');
  const [selectedAppointmentType, setSelectedAppointmentType] = useState<AppointmentType | null>(null);
  const [selectPatientAndTypeModal, setSelectPatientAndTypeModal] = useState<{
    mode: 'schedule' | 'recurring';
    rangeKey: string;
  } | null>(null);
  const [dragModal, setDragModal] = useState<null | { date: string; startTime: string; endTime: string }>(null);
  const [isBooking, setIsBooking] = useState<boolean>(false);
  const [patientsLoading, setPatientsLoading] = useState<boolean>(false);
  const [showDayLimitModal, setShowDayLimitModal] = useState<boolean>(false);

  // -------------------------------------------------------------------------
  // Refs
  // -------------------------------------------------------------------------

  const autoSelectedOnceRef = useRef<boolean>(false);
  const preloadHandledRef = useRef<boolean>(false);
  const lastScheduleTickRef = useRef<number>(0);
  const lastCancelTickRef = useRef<number>(0);

  // -------------------------------------------------------------------------
  // Computed memos
  // -------------------------------------------------------------------------

  const baseAppointmentTypes = useMemo(() => appointmentTypes, [appointmentTypes]);

  const effectivePatients = useMemo(() => {
    const needsMock =
      mockAvailability ||
      (() => {
        try {
          return !localStorage.getItem('psi-calendar-tour-done');
        } catch {
          return false;
        }
      })();
    if (!needsMock) return patients;
    return ONBOARDING_MOCK_PATIENTS;
  }, [patients, mockAvailability]);

  const selectedPatient = useMemo(
    () => effectivePatients.find(p => (selectedProcessId ? p.process === selectedProcessId : p.email === selectedPatientEmail)),
    [effectivePatients, selectedProcessId, selectedPatientEmail],
  );

  const selectPatient = useCallback((email: string, processId?: string) => {
    setSelectedPatientEmail(email);
    setSelectedProcessId(email ? (processId ?? '') : '');
  }, []);

  const compatibleAppointmentTypes = useMemo(
    () => getCompatibleAppointmentTypes(baseAppointmentTypes, selectedPatient?.processType as unknown as string),
    [baseAppointmentTypes, selectedPatient?.processType],
  );

  // -------------------------------------------------------------------------
  // Computed (inline, not memoized)
  // -------------------------------------------------------------------------

  const isSchedulingMode =
    !isRescheduleMode &&
    !editMode &&
    !isBusy &&
    !blockAll &&
    !blockScheduling &&
    !!selectedPatientEmail &&
    !!selectedAppointmentType;

  const isSchedulingIntent = !!selectedPatientEmail && !!selectedAppointmentType && !editMode;

  // -------------------------------------------------------------------------
  // setDragModalSafe — enforces 10h daily session limit
  // -------------------------------------------------------------------------

  const MAX_DAILY_SESSION_MINUTES = 600;

  const setDragModalSafe = useCallback(
    (modal: { date: string; startTime: string; endTime: string } | null) => {
      if (!modal) {
        setDragModal(null);
        return;
      }
      const dayMinutes = sessions.reduce((acc, s) => {
        if (s.date !== modal.date) return acc;
        if (s.status === 'cancelled' || s.status === 'blocked' || s.status === 'free') return acc;
        return acc + (s.duration || 60);
      }, 0);
      if (dayMinutes >= MAX_DAILY_SESSION_MINUTES) {
        setShowDayLimitModal(true);
        return;
      }
      setDragModal(modal);
    },
    [sessions],
  );

  // -------------------------------------------------------------------------
  // Effects: appointment type auto-selection
  // -------------------------------------------------------------------------

  // Fallback: if no type is selected yet, auto-select a compatible one
  useEffect(() => {
    if (!selectedPatientEmail) return;
    if (selectedAppointmentType) return;
    if (compatibleAppointmentTypes.length === 0) return;
    if (autoSelectedOnceRef.current) return;
    const ptName = (selectedPatient?.processType || '').toLowerCase();
    const auto =
      (ptName
        ? compatibleAppointmentTypes.find(t => t.name.toLowerCase() === ptName) ||
          compatibleAppointmentTypes.find(t => t.name.toLowerCase().includes(ptName))
        : undefined) || compatibleAppointmentTypes[0];
    if (auto && selectedAppointmentTypeId !== auto.id) {
      setSelectedAppointmentTypeId(auto.id);
      setSelectedAppointmentType(auto);
      autoSelectedOnceRef.current = true;
    }
  }, [
    selectedPatientEmail,
    selectedPatient?.processType,
    compatibleAppointmentTypes,
    selectedAppointmentType,
    selectedAppointmentTypeId,
  ]);

  // Safety net: auto-select by name/service when booking modal opens but no type selected
  useEffect(() => {
    if (!dragModal) return;
    if (selectedAppointmentType) return;
    if (autoSelectedOnceRef.current) return;
    const ptName = (selectedPatient?.processType || '').toLowerCase().trim();
    if (!Array.isArray(baseAppointmentTypes) || baseAppointmentTypes.length === 0) return;
    let auto: AppointmentType | undefined;
    if (ptName) {
      auto =
        baseAppointmentTypes.find(
          t =>
            t.name.toLowerCase() === ptName ||
            String((t as unknown as { service?: string }).service || '').toLowerCase() === ptName,
        ) || baseAppointmentTypes.find(t => t.name.toLowerCase().includes(ptName));
    }
    if (!auto) {
      auto = compatibleAppointmentTypes[0] || baseAppointmentTypes[0];
    }
    if (auto) {
      setSelectedAppointmentTypeId(auto.id);
      setSelectedAppointmentType(auto);
      autoSelectedOnceRef.current = true;
    }
  }, [dragModal, selectedAppointmentType, selectedPatient?.processType, baseAppointmentTypes, compatibleAppointmentTypes]);

  // Reset guard when booking modal closes
  useEffect(() => {
    if (!dragModal) {
      autoSelectedOnceRef.current = false;
    }
  }, [dragModal]);

  // Keep type in sync with compatible list whenever patient changes
  useEffect(() => {
    if (!selectedPatientEmail) return;

    if (compatibleAppointmentTypes.length === 0) {
      if (selectedAppointmentTypeId) setSelectedAppointmentTypeId('');
      if (selectedAppointmentType) setSelectedAppointmentType(null);
      return;
    }

    const currentType = compatibleAppointmentTypes.find(type => type.id === selectedAppointmentTypeId);
    if (!currentType) {
      const firstType = compatibleAppointmentTypes[0];
      if (selectedAppointmentTypeId !== firstType.id) setSelectedAppointmentTypeId(firstType.id);
      if (selectedAppointmentType?.id !== firstType.id) setSelectedAppointmentType(firstType);
      return;
    }

    if (selectedAppointmentType?.id !== currentType.id) {
      setSelectedAppointmentType(currentType);
    }
  }, [selectedPatientEmail, compatibleAppointmentTypes, selectedAppointmentTypeId, selectedAppointmentType]);

  // -------------------------------------------------------------------------
  // Effect: open modal from external tick (openScheduleModalTick)
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!openScheduleModalTick) return;
    if (openScheduleModalTick === lastScheduleTickRef.current) return;
    // Always consume the tick to prevent re-openings later
    lastScheduleTickRef.current = openScheduleModalTick;
    if (editMode || isBusy || isRescheduleMode) return;

    const reloadPatients = async () => {
      setPatientsLoading(true);
      try {
        if (psychologist?.id) {
          invalidatePatientsCache(String(psychologist.id));
          patientsCacheRef.current = undefined;
          patientsCachePsychIdRef.current = undefined;
          const freshPatients = await fetchPatientsOnce(String(psychologist.id));
          setPatients(freshPatients as Patient[]);
        } else {
          console.warn('No hay psychologist.id disponible');
        }
      } catch (error) {
        console.error('Error recargando pacientes:', error);
      } finally {
        setPatientsLoading(false);
      }
    };

    reloadPatients();
    setSelectPatientAndTypeModal({ mode: 'schedule', rangeKey: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openScheduleModalTick, editMode, isBusy, psychologist.id, fetchPatientsOnce]);

  // -------------------------------------------------------------------------
  // Effect: preloadProcessId — pre-selects patient by process ID
  // If the patient already has a next session, just shows the calendar.
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!preloadProcessId || preloadHandledRef.current) return;
    if (editMode || isBusy || isRescheduleMode) return;
    // Link abierto en frío: esperar a que la psicóloga esté cargada antes de consumir el preload,
    // si no el fetch de pacientes no se dispara y la preselección se pierde.
    if (!psychologist?.id) return;

    preloadHandledRef.current = true;

    const openWithPreload = async () => {
      // Try cache first
      const cachedMatch = patients.find(p => p.process === preloadProcessId);
      if (cachedMatch) {
        onPreloadConsumed?.();
        if (preloadForceSelect || !cachedMatch.hasNextSessionDate) selectPatient(cachedMatch.email, cachedMatch.process);
        return;
      }

      // Cache miss — fetch fresh
      setPatientsLoading(true);
      try {
        if (psychologist?.id) {
          invalidatePatientsCache(String(psychologist.id));
          patientsCacheRef.current = undefined;
          patientsCachePsychIdRef.current = undefined;

          const freshPatients = await fetchPatientsOnce(String(psychologist.id));
          setPatients(freshPatients as Patient[]);

          const match = (freshPatients as Patient[]).find(p => p.process === preloadProcessId);
          if (match) {
            onPreloadConsumed?.();
            if (preloadForceSelect || !match.hasNextSessionDate) selectPatient(match.email, match.process);
          }
        }
      } catch (error) {
        console.error('Error recargando pacientes para preload:', error);
      } finally {
        setPatientsLoading(false);
      }
    };

    openWithPreload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloadProcessId, editMode, isBusy, psychologist?.id, fetchPatientsOnce]);

  // -------------------------------------------------------------------------
  // Effect: cancelSchedulingTick — reset all selection state
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!cancelSchedulingTick) return;
    if (cancelSchedulingTick === lastCancelTickRef.current) return;
    lastCancelTickRef.current = cancelSchedulingTick;
    setSelectedPatientEmail('');
    setSelectedProcessId('');
    setSelectedAppointmentTypeId('');
    setSelectedAppointmentType(null);
  }, [cancelSchedulingTick]);

  // -------------------------------------------------------------------------
  // Effect: notify parent about scheduling intent
  // -------------------------------------------------------------------------

  useEffect(() => {
    onSchedulingModeChange?.(
      isSchedulingIntent,
      isSchedulingIntent ? (selectedPatient?.name ?? undefined) : undefined,
      isSchedulingIntent ? (selectedPatient?.process ?? undefined) : undefined,
    );
  }, [isSchedulingIntent, onSchedulingModeChange, selectedPatient?.name, selectedPatient?.process]);

  // -------------------------------------------------------------------------
  // handleBookingConfirm — main booking action for BookSessionModal
  // -------------------------------------------------------------------------

  const handleBookingConfirm = useCallback(async () => {
    if (!dragModal) return;

    const patientForScheduling = resolvePatientForScheduling(
      selectedPatient,
      effectivePatients,
      selectedPatientEmail,
    );
    if (!patientForScheduling) {
      showError('Seleccioná un paciente para continuar.');
      return;
    }

    const isMockPatient = patientForScheduling.email.endsWith('@demo.psi');

    const processId = patientForScheduling.process;
    if (!isMockPatient && !processId) {
      showError('El paciente seleccionado no tiene un proceso activo.');
      return;
    }

    const effectiveAppointmentType = resolveEffectiveAppointmentType(
      selectedAppointmentType,
      baseAppointmentTypes,
      patientForScheduling.processType,
      isMockPatient,
    );
    if (!effectiveAppointmentType) {
      showError('Seleccioná un tipo de cita válido.');
      return;
    }
    if (!isMockPatient && patientForScheduling.processType) {
      if (!validateAppointmentTypeMatch(effectiveAppointmentType, patientForScheduling.processType)) {
        showError('El tipo de cita elegido no corresponde con el proceso del paciente.');
        return;
      }
    }

    if (isMockPatient) {
      try {
        window.dispatchEvent(new CustomEvent('psi-booking-mock-started'));
      } catch {
        /* noop */
      }
      setIsBooking(true);
      setDragModal(null);
      selectPatient('');
      setSelectedAppointmentTypeId('');
      setSelectedAppointmentType(null);
      await new Promise(r => setTimeout(r, 800));
      showSuccess('Sesión de prueba agendada (demo)');
      setIsBooking(false);
      try {
        window.dispatchEvent(new CustomEvent('psi-booking-mock-completed'));
      } catch {
        /* noop */
      }
      return;
    }

    // Activate large loading and close modal immediately
    setIsBooking(true);
    setDragModal(null);
    selectPatient('');
    setSelectedAppointmentTypeId('');
    setSelectedAppointmentType(null);

    const yyyy = dragModal.date;
    const hhmm = dragModal.startTime;
    const datePayload = `${yyyy} ${hhmm}:00`;
    const finalProcessId = patientForScheduling.process as string;

    try {
      const scheduleResult = await apiCoreAdapter.scheduleSession({
        process_id: finalProcessId,
        date: (() => {
          try {
            // Argentina como fallback (NO el navegador): el grid se renderiza en
            // ese huso, así que la conversión a UTC debe usar el mismo. Evita el
            // desfase si la psicóloga tiene el reloj del SO en otro timezone.
            const tz = selectedTimeZone || BA_TIMEZONE;
            const utcDate = localTzToUTC(yyyy, hhmm, tz);
            return utcDate.toISOString();
          } catch {
            return datePayload;
          }
        })(),
        scheduled_by: (() => {
          try {
            const impersonatorId = getImpersonatorId();
            if (impersonatorId) return impersonatorId;
            const session = getSessionData();
            if (session?.user?.email) return session.user.email;
          } catch {
            /* noop */
          }
          return 'platform';
        })(),
        bypass_constraints: isImpersonated(),
      });

      if (!scheduleResult.success) {
        const errorMessage = scheduleResult.reason || scheduleResult.error || 'Error al agendar la sesión';
        throw new Error(errorMessage);
      }

      let localDate = dragModal.date;
      let localTime = dragModal.startTime;
      if (scheduleResult.datetime) {
        try {
          const parts = isoToLocalParts(scheduleResult.datetime);
          localDate = parts.date;
          localTime = parts.time;
        } catch {
          // If parsing fails, use original values
        }
      }

      // Derive session duration from selected appointment type
      let sessionDuration = 60;
      if (selectedAppointmentType?.id) {
        const aptType = baseAppointmentTypes.find(
          (apt: AppointmentType) => apt.id === selectedAppointmentType.id,
        );
        if (aptType && aptType.duration) {
          sessionDuration = aptType.duration;
        }
      }

      const ok = scheduleDirect({
        id: '',
        psychologistId: psychologist.id,
        date: localDate,
        time: localTime,
        modality: 'online',
        patientName: patientForScheduling.name || 'Patient',
        patientEmail: patientForScheduling.email,
        patientPhone: patientForScheduling.phone,
        specialty: psychologist.specialties[0] || 'general',
        status: 'scheduled',
        process_id: finalProcessId,
        appointment_type: selectedAppointmentType?.id,
        duration: sessionDuration,
      });

      if (ok.ok) {
        patientsCacheRef.current = undefined;
        patientsCachePsychIdRef.current = undefined;

        const endOfWeekDate = new Date(startOfWeek);
        endOfWeekDate.setDate(startOfWeek.getDate() + 6);
        const weekKey = `${startOfWeek.toISOString().slice(0, 10)}_${endOfWeekDate.toISOString().slice(0, 10)}_${psychologist.id}`;
        invalidateSessionsCache(weekKey);
        showSuccess('Sesión agendada exitosamente');

        await loadData();
      }
    } catch (e) {
      console.error(e);
      const errorMessage =
        e instanceof Error ? e.message : 'Error al agendar la sesión. Por favor, intenta nuevamente.';
      showError(errorMessage);
    } finally {
      setIsBooking(false);
    }
  }, [
    dragModal,
    selectedPatient,
    effectivePatients,
    selectedPatientEmail,
    selectPatient,
    selectedAppointmentType,
    baseAppointmentTypes,
    selectedTimeZone,
    psychologist,
    startOfWeek,
    scheduleDirect,
    loadData,
    showError,
    showSuccess,
    patientsCacheRef,
    patientsCachePsychIdRef,
  ]);

  // -------------------------------------------------------------------------
  // Patient timezone
  // -------------------------------------------------------------------------

  // Huso del paciente tomado de las sesiones — la misma fuente que usa el resto de la
  // agenda (SessionItem, SessionDetailsModal, RescheduleModal, CalendarCell). La lista de
  // pacientes no lo trae siempre: los que entran por el merge de process-sessions-data
  // llegan sin huso, y el modal de confirmación se quedaba sin la hora del paciente.
  const patientTimezoneFromSessions = useMemo(() => {
    const email = (selectedPatient?.email || selectedPatientEmail || '').toLowerCase();
    const processId = selectedPatient?.process;
    const match = sessions.find(s =>
      (processId && s.process_id === processId) ||
      (email && (s.patient?.email || '').toLowerCase() === email)
    );
    return match?.patient?.timezone || undefined;
  }, [sessions, selectedPatient?.email, selectedPatient?.process, selectedPatientEmail]);

  // Último recurso: paciente sin sesiones en la semana visible → buscar en el histórico.
  const [patientTimezoneFromHistory, setPatientTimezoneFromHistory] = useState<string | undefined>(undefined);

  const patientTimezone =
    selectedPatient?.timezone || patientTimezoneFromSessions || patientTimezoneFromHistory;

  // Al cambiar de paciente, descartar el huso resuelto del anterior.
  useEffect(() => {
    setPatientTimezoneFromHistory(undefined);
  }, [selectedPatientEmail]);

  useEffect(() => {
    const processId = selectedPatient?.process;
    if (!isSchedulingMode || !processId) return;
    if (selectedPatient?.timezone || patientTimezoneFromSessions) return;

    let cancelled = false;
    apiCoreAdapter.getPatientTimezoneFromSessions(processId)
      .then(tz => { if (!cancelled && tz) setPatientTimezoneFromHistory(tz); })
      .catch(() => { /* noop */ });
    return () => { cancelled = true; };
  }, [isSchedulingMode, selectedPatient?.process, selectedPatient?.timezone, patientTimezoneFromSessions]);

  // -------------------------------------------------------------------------
  // DST dates: fetch dst_start/dst_end for patient timezone
  // -------------------------------------------------------------------------

  const [dstDates, setDstDates] = useState<{ start: string; end: string; diffHours: number } | null>(null);

  useEffect(() => {
    const patientTz = patientTimezone;
    if (!patientTz || !isSchedulingMode) {
      setDstDates(null);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const tzInfo = await apiCoreAdapter.getTimezoneInfo(patientTz);
        if (cancelled) return;
        if (tzInfo.dst_start && tzInfo.dst_end) {
          const parseOffset = (s: string) => { const [h, m] = s.split(':').map(Number); return h + (m || 0) / 60; };
          const utcH = parseOffset(tzInfo.utc_offset);
          const dstH = tzInfo.dst_offset ? parseOffset(tzInfo.dst_offset) : utcH;
          const diffHours = Math.abs(dstH - utcH) || 1;
          setDstDates({ start: tzInfo.dst_start, end: tzInfo.dst_end, diffHours });
        } else {
          setDstDates(null);
        }
      } catch {
        if (!cancelled) setDstDates(null);
      }
    })();

    return () => { cancelled = true; };
  }, [patientTimezone, isSchedulingMode]);

  // -------------------------------------------------------------------------
  // Return
  // -------------------------------------------------------------------------

  return {
    selectedPatientEmail,
    setSelectedPatientEmail,
    selectedProcessId,
    selectPatient,
    selectedAppointmentTypeId,
    setSelectedAppointmentTypeId,
    selectedAppointmentType,
    setSelectedAppointmentType,
    selectPatientAndTypeModal,
    setSelectPatientAndTypeModal,
    dragModal,
    setDragModal,
    setDragModalSafe,
    isBooking,
    patientsLoading,
    showDayLimitModal,
    setShowDayLimitModal,
    effectivePatients,
    selectedPatient,
    compatibleAppointmentTypes,
    isSchedulingMode,
    isSchedulingIntent,
    handleBookingConfirm,
    dstDates,
    patientTimezone,
  };
}
